import { TABLE_ROW_MIN_HEIGHT } from "../constants";
import {
  TableBorderRadiusEnum,
  TableBorderStyle,
  TableCellVerticalAlignment,
  TableHeaderPosition,
  type ITableSettings,
  type ITableSpecificSettings,
} from "../types";

const OLD_ROUND_TO_NEW: Record<string, TableBorderRadiusEnum> = {
  "0": TableBorderRadiusEnum.None,
  "8px": TableBorderRadiusEnum.Small,
  "16px": TableBorderRadiusEnum.Medium,
  "32px": TableBorderRadiusEnum.Large,
};

export const ensureTableElementSettings = (
  elementSettings: Record<string, unknown> | null = {},
) => {
  const {
    tableData,
    tableRows,
    tableCols,
    tableCellFontSize,
    tableCellBorderWidth,
    tableCellBorderStyle,
    tableCellBorderColor,
    tableCellTextColor,
    tableCellBgColor,
    tableCellTextAlignment,
    tableCellTextOrientation,
    tableCellVerticalAlignment,
    tableHasAlternatingRows,
    tableAlternatingRowsBgColor,
    tableAlternatingRowsBorderColor,
    tableAlternatingRowsTextColor,
    tableHasHeader,
    tableHeaderPosition,
    tableHeaderBgColor,
    tableHeaderBorderColor,
    tableHeaderTextColor,
    tableColWidths,
    tableRowHeights,
    tableMobileLayout,
    tableAllowSorting,
    tableAllowFiltering,
    tableFrozenRowsCount,
    round,
  } = elementSettings as Partial<ITableSettings>;

  const baseSettings = {
    horizontalAlignment: "center",
    width: "100%",
    height: 5 * TABLE_ROW_MIN_HEIGHT,
    ...elementSettings,
    name: "Table",
  };

  const settings: ITableSpecificSettings = {
    tableCols: tableCols || 3,
    tableRows: tableRows || 5,
    tableColWidths: tableColWidths || [33.3, 33.4, 33.3],
    tableRowHeights: tableRowHeights || [],
    tableData: tableData ?? [],
    //
    // Header
    tableHasHeader: tableHasHeader ?? false,
    tableHeaderPosition: tableHeaderPosition ?? TableHeaderPosition.Top,
    tableHeaderBgColor: tableHeaderBgColor ?? "#dddddd",
    tableHeaderBorderColor: tableHeaderBorderColor ?? "#aaaaaa",
    tableHeaderTextColor: tableHeaderTextColor ?? "var(--paragraph-color)",
    //
    // Alternating rows
    tableHasAlternatingRows: tableHasAlternatingRows ?? false,
    tableAlternatingRowsBgColor: tableAlternatingRowsBgColor ?? "#eeeeee",
    tableAlternatingRowsBorderColor:
      tableAlternatingRowsBorderColor ?? "#aaaaaa",
    tableAlternatingRowsTextColor:
      tableAlternatingRowsTextColor ?? "var(--paragraph-color)",
    //
    //
    tableCellFontSize: tableCellFontSize ?? 16,
    tableCellTextColor: tableCellTextColor ?? "var(--paragraph-color)",
    tableCellBgColor: tableCellBgColor ?? "#ffffff",
    tableCellBorderColor: tableCellBorderColor ?? "#aaaaaa",
    tableCellBorderStyle: TableBorderStyle.Solid,
    tableCellBorderWidth: tableCellBorderWidth ?? 1,
    tableCellTextAlignment: tableCellTextAlignment ?? "left",
    tableCellTextOrientation: tableCellTextOrientation ?? "ltr",
    tableCellVerticalAlignment:
      tableCellVerticalAlignment ?? TableCellVerticalAlignment.Center,
    tableMobileLayout: tableMobileLayout ?? "table",
    tableAllowSorting: tableAllowSorting ?? false,
    tableAllowFiltering: tableAllowFiltering ?? false,
    tableFrozenRowsCount: tableFrozenRowsCount ?? 0,
    //
    round: TableBorderRadiusEnum.Small,
  };

  if (!tableColWidths) {
    settings.tableColWidths = Array.from(
      { length: settings.tableCols },
      () => 100 / settings.tableCols,
    );
  }

  if (
    tableCellBorderStyle &&
    [TableBorderStyle.Dashed, TableBorderStyle.Dotted].includes(
      tableCellBorderStyle,
    )
  ) {
    settings.tableCellBorderStyle = tableCellBorderStyle;
  }

  if (round) {
    if (
      [
        TableBorderRadiusEnum.None,
        TableBorderRadiusEnum.Small,
        TableBorderRadiusEnum.Medium,
        TableBorderRadiusEnum.Large,
      ].includes(round)
    ) {
      settings.round = round;
    } else {
      settings.round = OLD_ROUND_TO_NEW[round] ?? TableBorderRadiusEnum.Small;
    }
  }

  return { ...baseSettings, ...settings } as Required<ITableSettings>;
};
